import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, Calendar, ClipboardList, Building, Megaphone, Share2, PenTool, Search, Mail, Phone, Database, Target, HeadphonesIcon, UserPlus, Star, Calculator, FileText, Users, Code, BarChart3, ChevronDown, ArrowRight, Quote, ArrowRightLeft, LayoutDashboard, Clock, Settings, Shield, Globe, Palette, LineChart } from 'lucide-react';
// --- Data ---
const DEPARTMENTS = [{
  name: 'Marketing',
  color: 'purple',
  bgClass: 'bg-purple/10',
  borderClass: 'border-purple/20',
  textClass: 'text-purple',
  roles: [{
    title: 'Marketing Manager',
    hours: '~28 hrs/week',
    icon: <Target size={24} data-id="element-2724" data-mp="1ynpiqhz3lioa" />,
    desc: 'Campaign strategy, budget allocation, performance tracking, ROI analysis',
    tasks: [{
      desc: 'Build & manage a 30-day content calendar',
      time: '3 hrs/week'
    }, {
      desc: 'Run A/B tests on ad copy & auto-select the winner',
      time: 'Passive'
    }, {
      desc: 'Generate weekly paid ads performance report',
      time: '2 hrs/week'
    }, {
      desc: 'Flag underperforming ads & pause them automatically',
      time: 'Passive'
    }, {
      desc: 'Monitor competitor campaigns & summarize weekly',
      time: '1 hr/week'
    }, {
      desc: 'Auto-stop spending when budget threshold is hit',
      time: 'Passive'
    }, {
      desc: 'Generate monthly marketing ROI report',
      time: '3 hrs/month'
    }, {
      desc: 'Monitor brand mentions across all platforms in real-time',
      time: 'Passive'
    }, {
      desc: 'Create lookalike audiences from best-converting customers',
      time: '2 hrs/week'
    }, {
      desc: 'Track and report ROAS by campaign, ad set & creative',
      time: '2 hrs/week'
    }],
    total: '~28 hrs/week',
    tools: ['Google Ads', 'Meta Ads', 'Google Analytics', 'HubSpot']
  }, {
    title: 'Email Marketer',
    hours: '~20 hrs/week',
    icon: <Mail size={24} data-id="element-2725" data-mp="pcwnlrcsvlyh" />,
    desc: 'Drip campaigns, newsletters, segmentation, A/B testing, deliverability',
    tasks: [{
      desc: 'Build automated nurture sequences for new leads',
      time: '3 hrs/week'
    }, {
      desc: 'A/B test subject lines and send-time optimization',
      time: 'Passive'
    }, {
      desc: 'Segment audiences based on behavior and engagement',
      time: '2 hrs/week'
    }, {
      desc: 'Generate weekly email performance report',
      time: '1 hr/week'
    }, {
      desc: 'Clean email lists and remove bounced/inactive contacts',
      time: 'Passive'
    }, {
      desc: 'Create and schedule weekly newsletter',
      time: '3 hrs/week'
    }, {
      desc: 'Monitor deliverability scores and spam complaints',
      time: 'Passive'
    }, {
      desc: 'Set up triggered emails for cart abandonment, welcome, re-engagement',
      time: '2 hrs/week'
    }, {
      desc: 'Track revenue attribution per email campaign',
      time: '1 hr/week'
    }, {
      desc: 'Auto-update segments when contact properties change',
      time: 'Passive'
    }],
    total: '~20 hrs/week',
    tools: ['Mailchimp', 'ActiveCampaign', 'HubSpot', 'Klaviyo']
  }]
}, {
  name: 'Social Media',
  color: 'teal',
  bgClass: 'bg-teal/10',
  borderClass: 'border-teal/20',
  textClass: 'text-teal',
  roles: [{
    title: 'Social Media Manager',
    hours: '~25 hrs/week',
    icon: <Share2 size={24} data-id="element-2726" data-mp="g0uqp5rqbu63" />,
    desc: 'Content scheduling, engagement, community management, analytics',
    tasks: [{
      desc: 'Schedule & publish content across all platforms daily',
      time: 'Passive'
    }, {
      desc: 'Auto-resize images to correct specs per platform',
      time: 'Passive'
    }, {
      desc: 'Auto-respond to DMs with initial reply & routing',
      time: 'Passive'
    }, {
      desc: 'Alert when a post gets unusually high engagement',
      time: 'Passive'
    }, {
      desc: 'Track follower growth rate & flag drops weekly',
      time: '1 hr/week'
    }, {
      desc: 'Monitor & auto-hide inappropriate comments',
      time: 'Passive'
    }, {
      desc: 'Clone top-performing content across platforms',
      time: '2 hrs/week'
    }, {
      desc: 'Generate monthly audience growth & engagement report',
      time: '3 hrs/month'
    }, {
      desc: 'Track which content formats drive the most website traffic',
      time: '1 hr/week'
    }, {
      desc: 'Monitor competitor social accounts & summarize monthly',
      time: '2 hrs/month'
    }],
    total: '~25 hrs/week',
    tools: ['Buffer', 'Hootsuite', 'Later', 'Canva']
  }]
}, {
  name: 'Content',
  color: 'purple-alt',
  bgClass: 'bg-[#852FF4]/10',
  borderClass: 'border-[#852FF4]/20',
  textClass: 'text-[#852FF4]',
  roles: [{
    title: 'Content Manager',
    hours: '~20 hrs/week',
    icon: <PenTool size={24} data-id="element-2727" data-mp="3nmxavrahtouv" />,
    desc: 'Blog posts, SEO content, newsletters, brand voice copy',
    tasks: [{
      desc: 'Generate SEO-optimized blog outlines from keyword research',
      time: '3 hrs/week'
    }, {
      desc: 'Repurpose each blog post into social, email & LinkedIn',
      time: '2 hrs/week'
    }, {
      desc: 'Auto-update posts on a scheduled content calendar',
      time: 'Passive'
    }, {
      desc: 'Generate video titles, descriptions & tags from transcript',
      time: '1 hr/week'
    }, {
      desc: 'Auto-create a blog post from a video transcript',
      time: '2 hrs/week'
    }, {
      desc: 'Monitor & flag blog posts with declining rankings for refresh',
      time: 'Passive'
    }, {
      desc: 'Generate chapter markers & timestamps for videos',
      time: 'Passive'
    }, {
      desc: 'Auto-send a weekly content digest to your email list',
      time: 'Passive'
    }, {
      desc: 'Track content ROI (traffic, leads, conversions) monthly',
      time: '2 hrs/month'
    }, {
      desc: 'Generate a quarterly content audit report',
      time: '3 hrs/quarter'
    }],
    total: '~20 hrs/week',
    tools: ['WordPress', 'Notion', 'Google Docs', 'Grammarly']
  }]
}, {
  name: 'SEO',
  color: 'steel',
  bgClass: 'bg-steel/10',
  borderClass: 'border-steel/20',
  textClass: 'text-steel',
  roles: [{
    title: 'SEO Specialist',
    hours: '~15 hrs/week',
    icon: <Search size={24} data-id="element-2728" data-mp="1p4ilt4ux0oj8" />,
    desc: 'Rankings, technical SEO, audits, schema, competitor analysis',
    tasks: [{
      desc: 'Track keyword rankings daily & report weekly changes',
      time: 'Passive'
    }, {
      desc: 'Auto-generate meta titles & descriptions for every new page',
      time: 'Passive'
    }, {
      desc: 'Alert when a previously ranking page disappears from Google',
      time: 'Passive'
    }, {
      desc: 'Run a monthly technical SEO audit & summarize issues',
      time: '3 hrs/month'
    }, {
      desc: 'Monitor Core Web Vitals & alert when they drop',
      time: 'Passive'
    }, {
      desc: 'Flag keyword cannibalization (two pages fighting for same keyword)',
      time: 'Passive'
    }, {
      desc: 'Auto-generate FAQ schema markup for blog posts',
      time: 'Passive'
    }, {
      desc: 'Alert when a competitor outranks you for a tracked keyword',
      time: 'Passive'
    }, {
      desc: 'Monitor & respond to Google Business Profile weekly',
      time: '1 hr/week'
    }, {
      desc: 'Generate a monthly local SEO report for each service area',
      time: '2 hrs/month'
    }],
    total: '~15 hrs/week',
    tools: ['Ahrefs', 'SEMrush', 'Google Search Console', 'Screaming Frog']
  }]
}, {
  name: 'Sales',
  color: 'red-cta',
  bgClass: 'bg-[#FF000A]/10',
  borderClass: 'border-[#FF000A]/20',
  textClass: 'text-[#FF000A]',
  roles: [{
    title: 'Sales Manager',
    hours: '~25 hrs/week',
    icon: <Briefcase size={24} data-id="element-2729" data-mp="32j3bkjuqfqed" />,
    desc: 'Pipeline management, forecasting, follow-ups, deal tracking',
    tasks: [{
      desc: 'Auto-capture leads from every form & add to CRM pipeline',
      time: 'Passive'
    }, {
      desc: 'Trigger personalized nurture sequence the moment a lead enters',
      time: 'Passive'
    }, {
      desc: 'Score leads automatically by behavior & demographics',
      time: 'Passive'
    }, {
      desc: 'Alert when a hot lead visits your pricing page 3+ times',
      time: 'Passive'
    }, {
      desc: 'Send a contract automatically when a deal hits proposal stage',
      time: '2 hrs/week'
    }, {
      desc: 'Alert the moment a contract is opened, viewed, or signed',
      time: 'Passive'
    }, {
      desc: 'Generate weekly pipeline health & revenue forecast report',
      time: '3 hrs/week'
    }, {
      desc: 'Re-engage leads that went cold 60+ days ago automatically',
      time: 'Passive'
    }, {
      desc: 'Build & enrich lead lists from LinkedIn funnel to CRM',
      time: '3 hrs/week'
    }, {
      desc: 'Trigger a referral request sequence 90 days after a client starts',
      time: 'Passive'
    }],
    total: '~25 hrs/week',
    tools: ['HubSpot', 'Salesforce', 'PandaDocs', 'Close']
  }, {
    title: 'Appointment Setter',
    hours: '~25 hrs/week',
    icon: <Phone size={24} data-id="element-2730" data-mp="3gbdqwjq0op6v" />,
    desc: 'Lead response, qualification, booking, follow-up, pipeline feeding',
    tasks: [{
      desc: 'Respond to every inbound lead within 60 seconds via chat or SMS',
      time: 'Passive'
    }, {
      desc: 'Qualify leads via AI chatbot before they ever hit your calendar',
      time: 'Passive'
    }, {
      desc: 'Book discovery calls directly from conversations — zero back-and-forth',
      time: 'Passive'
    }, {
      desc: 'Send 3-part reminder sequence before every booked call',
      time: 'Passive'
    }, {
      desc: 'Follow up with no-shows automatically within 30 minutes',
      time: 'Passive'
    }, {
      desc: 'Re-engage cold leads with a timed reactivation sequence',
      time: 'Passive'
    }, {
      desc: "Send relevant case study based on prospect's industry automatically",
      time: 'Passive'
    }, {
      desc: 'Alert the sales team the moment a lead scores above threshold',
      time: 'Passive'
    }, {
      desc: 'Generate daily "calls booked today" report for the team',
      time: '1 hr/day'
    }, {
      desc: 'Track show-up rate & no-show rate and report weekly',
      time: '1 hr/week'
    }],
    total: '~25 hrs/week',
    tools: ['Calendly', 'GoHighLevel', 'ChatBot', 'Instantly']
  }]
}, {
  name: 'Lead Generation',
  color: 'teal',
  bgClass: 'bg-teal/10',
  borderClass: 'border-teal/20',
  textClass: 'text-teal',
  roles: [{
    title: 'Lead Generation Specialist',
    hours: '~18 hrs/week',
    icon: <Megaphone size={24} data-id="element-2731" data-mp="124wzr52g971c" />,
    desc: 'Prospecting, cold outreach, list building, qualification',
    tasks: [{
      desc: 'Build & enrich targeted lead lists from LinkedIn & web',
      time: '3 hrs/week'
    }, {
      desc: 'Verify email addresses before adding to any outreach sequence',
      time: 'Passive'
    }, {
      desc: 'Launch personalized cold email sequences to new prospect lists',
      time: '2 hrs/week'
    }, {
      desc: 'Identify companies visiting your website via reverse IP lookup',
      time: 'Passive'
    }, {
      desc: 'Trigger "hot lead" alert when someone visits 3+ pages',
      time: 'Passive'
    }, {
      desc: 'Auto-qualify leads via chatbot before hitting the calendar',
      time: 'Passive'
    }, {
      desc: 'Monitor competitor review sites for unhappy customers',
      time: 'Passive'
    }, {
      desc: "Auto-send a relevant case study based on prospect's industry",
      time: 'Passive'
    }, {
      desc: 'Generate weekly report of new leads by source & quality',
      time: '2 hrs/week'
    }, {
      desc: 'Alert when a hot prospect returns to your website',
      time: 'Passive'
    }],
    total: '~18 hrs/week',
    tools: ['Apollo', 'LinkedIn Sales Nav', 'Hunter.io', 'Instantly']
  }]
}, {
  name: 'Project Management',
  color: 'purple',
  bgClass: 'bg-purple/10',
  borderClass: 'border-purple/20',
  textClass: 'text-purple',
  roles: [{
    title: 'Project Manager',
    hours: '~22 hrs/week',
    icon: <ClipboardList size={24} data-id="element-2732" data-mp="3ednfoe49zlng" />,
    desc: 'Task breakdown, deadlines, accountability, delivery tracking',
    tasks: [{
      desc: 'Auto-create a full project from template when client signs',
      time: 'Passive'
    }, {
      desc: 'Assign tasks based on team workload automatically',
      time: 'Passive'
    }, {
      desc: 'Send automated reminders 48 hours before every deadline',
      time: 'Passive'
    }, {
      desc: 'Escalate overdue tasks to manager after 3 days',
      time: 'Passive'
    }, {
      desc: 'Generate weekly status report for every active project',
      time: '3 hrs/week'
    }, {
      desc: 'Flag when a project goes over budget immediately',
      time: 'Passive'
    }, {
      desc: 'Generate "must do this week" briefing every Monday',
      time: '1 hr/week'
    }, {
      desc: 'Alert when a critical path task is delayed',
      time: 'Passive'
    }, {
      desc: 'Notify client automatically when a milestone is completed',
      time: 'Passive'
    }, {
      desc: 'Auto-redistribute work if priorities or deadlines shift',
      time: '2 hrs/week'
    }],
    total: '~22 hrs/week',
    tools: ['Asana', 'Monday.com', 'ClickUp', 'Notion']
  }]
}, {
  name: 'Operations',
  color: 'blue',
  bgClass: 'bg-[#3B82F6]/10',
  borderClass: 'border-[#3B82F6]/20',
  textClass: 'text-[#3B82F6]',
  roles: [{
    title: 'Operations Manager',
    hours: '~18 hrs/week',
    icon: <Settings size={24} data-id="element-2733" data-mp="2n9w5o4f2n5yl" />,
    desc: 'Process automation, SOP management, vendor coordination, tool integration',
    tasks: [{
      desc: "Connect any two tools that don't natively integrate",
      time: '2 hrs/week'
    }, {
      desc: 'Pull data from all platforms into one searchable dashboard',
      time: 'Passive'
    }, {
      desc: 'Alert when any SOP steps take 2x longer than benchmark',
      time: 'Passive'
    }, {
      desc: 'Monitor uptime & auto-notify if any service drops',
      time: 'Passive'
    }, {
      desc: 'Auto-renew/cancel subscriptions in a central log monthly',
      time: 'Passive'
    }, {
      desc: 'Sync data between all connected platforms every 15 minutes',
      time: 'Passive'
    }, {
      desc: 'Generate a weekly team productivity & status report',
      time: '2 hrs/week'
    }, {
      desc: 'Flag when new software or tools need security review',
      time: 'Passive'
    }, {
      desc: 'Auto-generate audit trail of who did what & when (key actions)',
      time: 'Passive'
    }, {
      desc: 'Categorize all transactions by vendor & type automatically',
      time: 'Passive'
    }],
    total: '~18 hrs/week',
    tools: ['Zapier', 'Make', 'Notion', 'Google Workspace']
  }]
}, {
  name: 'Executive Function',
  color: 'steel',
  bgClass: 'bg-steel/10',
  borderClass: 'border-steel/20',
  textClass: 'text-steel',
  roles: [{
    title: 'Executive Assistant',
    hours: '~22 hrs/week',
    icon: <Calendar size={24} data-id="element-2734" data-mp="26xqf0g5ba1br" />,
    desc: 'Calendar, inbox, travel, daily briefings, priority management',
    tasks: [{
      desc: 'Auto-sort emails into folders by sender, topic & urgency',
      time: 'Passive'
    }, {
      desc: 'Generate top-level briefings into 3 daily priority items',
      time: '1 hr/day'
    }, {
      desc: 'Auto-book discovery calls when a lead fills out a form',
      time: 'Passive'
    }, {
      desc: 'Draft quick responses to routine emails — you just approve',
      time: '3 hrs/week'
    }, {
      desc: 'Send 5-part reminder sequence before every meeting',
      time: 'Passive'
    }, {
      desc: 'Auto-generate meeting summary & action items after every call',
      time: 'Passive'
    }, {
      desc: 'Generate weekly agenda summary every Sunday evening',
      time: '1 hr/week'
    }, {
      desc: 'Stuff replies to common inquiries in your tone & voice',
      time: '2 hrs/week'
    }, {
      desc: "Alert when any important email hasn't been replied to in 48 hrs",
      time: 'Passive'
    }, {
      desc: 'Prioritize daily inbox based on urgency and sender importance',
      time: 'Passive'
    }],
    total: '~22 hrs/week',
    tools: ['Google Calendar', 'Gmail', 'Calendly', 'Notion']
  }]
}, {
  name: 'Customer Success',
  color: 'purple-alt',
  bgClass: 'bg-[#852FF4]/10',
  borderClass: 'border-[#852FF4]/20',
  textClass: 'text-[#852FF4]',
  roles: [{
    title: 'Customer Success Manager',
    hours: '~18 hrs/week',
    icon: <HeadphonesIcon size={24} data-id="element-2735" data-mp="2p157zn3hulh2" />,
    desc: 'Client check-ins, satisfaction tracking, retention, upsells',
    tasks: [{
      desc: 'Trigger full onboarding sequence the moment contract is signed',
      time: 'Passive'
    }, {
      desc: 'Send monthly progress updates to every client on autopilot',
      time: 'Passive'
    }, {
      desc: "Auto-create support tickets in the right team's queue",
      time: 'Passive'
    }, {
      desc: 'Acknowledge every ticket within 5 seconds',
      time: 'Passive'
    }, {
      desc: 'Send review request 7 days after every completed project',
      time: 'Passive'
    }, {
      desc: 'Alert when NPS or satisfaction drops below threshold',
      time: 'Passive'
    }, {
      desc: 'Generate weekly client health score dashboard',
      time: '2 hrs/week'
    }, {
      desc: 'Auto-send renewal reminders 30 days before contract expires',
      time: 'Passive'
    }, {
      desc: 'Auto-share 5-star reviews to social media',
      time: 'Passive'
    }, {
      desc: 'Flag at-risk clients based on engagement drop-off',
      time: 'Passive'
    }],
    total: '~18 hrs/week',
    tools: ['Intercom', 'HubSpot', 'Zendesk', 'Trustpilot']
  }, {
    title: 'Onboarding Specialist',
    hours: '~15 hrs/week',
    icon: <UserPlus size={24} data-id="element-2736" data-mp="2k938cj1uawfy" />,
    desc: 'Welcome sequences, data collection, setup guidance, first-value delivery',
    tasks: [{
      desc: 'Send welcome packet & contracts automatically on signup',
      time: 'Passive'
    }, {
      desc: 'Collect all necessary intake data via smart forms',
      time: '2 hrs/week'
    }, {
      desc: 'Guide users through initial setup with step-by-step sequences',
      time: '2 hrs/week'
    }, {
      desc: 'Schedule kickoff call automatically after intake is complete',
      time: 'Passive'
    }, {
      desc: 'Send "getting started" video series drip over first 7 days',
      time: 'Passive'
    }, {
      desc: "Alert team when a new client hasn't completed onboarding in 48 hrs",
      time: 'Passive'
    }, {
      desc: 'Generate onboarding completion report weekly',
      time: '1 hr/week'
    }, {
      desc: 'Auto-assign dedicated support contact based on plan tier',
      time: 'Passive'
    }, {
      desc: 'Collect first feedback survey 14 days after onboarding',
      time: 'Passive'
    }, {
      desc: 'Flag incomplete profiles and send reminder sequences',
      time: 'Passive'
    }],
    total: '~15 hrs/week',
    tools: ['Typeform', 'Calendly', 'Loom', 'HubSpot']
  }]
}, {
  name: 'HR',
  color: 'teal',
  bgClass: 'bg-teal/10',
  borderClass: 'border-teal/20',
  textClass: 'text-teal',
  roles: [{
    title: 'HR & Staff Manager',
    hours: '~15 hrs/week',
    icon: <Users size={24} data-id="element-2737" data-mp="ro468q1fyghm" />,
    desc: 'Hiring, onboarding, performance tracking, policy management',
    tasks: [{
      desc: 'Post job openings to multiple job boards simultaneously',
      time: '2 hrs/week'
    }, {
      desc: 'Auto-screen resumes & score against your requirements',
      time: 'Passive'
    }, {
      desc: 'Schedule interviews with qualified candidates automatically',
      time: 'Passive'
    }, {
      desc: "Alert manager when employee hasn't clocked in on time",
      time: 'Passive'
    }, {
      desc: 'Monitor team PTO and generate coverage reports',
      time: '1 hr/week'
    }, {
      desc: 'Generate weekly accessibility report by team member',
      time: '1 hr/week'
    }, {
      desc: 'Onboard new employee — accounts, welcome sequence, training assigned',
      time: '2 hrs/week'
    }, {
      desc: 'Off-board employee — revoke all access, archive, final checklist',
      time: '1 hr/event'
    }, {
      desc: 'Track certifications & training expiration dates',
      time: 'Passive'
    }, {
      desc: 'Generate quarterly performance review templates pre-filled with data',
      time: '3 hrs/quarter'
    }],
    total: '~15 hrs/week',
    tools: ['BambooHR', 'Gusto', 'Google Workspace', 'Notion']
  }]
}, {
  name: 'Finance',
  color: 'blue',
  bgClass: 'bg-[#60A5FA]/10',
  borderClass: 'border-[#60A5FA]/20',
  textClass: 'text-[#60A5FA]',
  roles: [{
    title: 'Financial Analyst',
    hours: '~15 hrs/week',
    icon: <BarChart3 size={24} data-id="element-2738" data-mp="22cmh3q8s7hl2" />,
    desc: 'P&L, cash flow, budget tracking, financial reporting, tax prep',
    tasks: [{
      desc: 'Auto-generate monthly P&L, balance sheet & cash flow statement',
      time: '3 hrs/month'
    }, {
      desc: 'Alert when any expense exceeds your set budget threshold',
      time: 'Passive'
    }, {
      desc: 'Categorize all transactions by vendor & type automatically',
      time: 'Passive'
    }, {
      desc: 'Generate monthly P&L summary automatically',
      time: '2 hrs/month'
    }, {
      desc: 'Track all accounts payable & flag invoices due within 7 days',
      time: 'Passive'
    }, {
      desc: 'Reconcile Stripe, PayPal & bank accounts monthly',
      time: '2 hrs/month'
    }, {
      desc: 'Alert when revenue is tracking below monthly target',
      time: 'Passive'
    }, {
      desc: 'Add estimated tax bill at end of each quarter to pay period',
      time: '3 hrs/quarter'
    }, {
      desc: 'Track accounts receivable aging & report weekly',
      time: '1 hr/week'
    }, {
      desc: 'Generate year-end financial summary automatically',
      time: '5 hrs/year'
    }],
    total: '~15 hrs/week',
    tools: ['QuickBooks', 'Xero', 'Stripe', 'Google Sheets']
  }, {
    title: 'Bookkeeper',
    hours: '~12 hrs/week',
    icon: <Calculator size={24} data-id="element-2739" data-mp="3p31xehc5390c" />,
    desc: 'Transaction logging, reconciliation, receipt tracking, expense management',
    tasks: [{
      desc: 'Categorize all transactions by vendor & account type automatically',
      time: 'Passive'
    }, {
      desc: 'Match bank transactions to invoices & flag any mismatches',
      time: 'Passive'
    }, {
      desc: 'Auto-generate monthly P&L, balance sheet & cash flow statement',
      time: '3 hrs/month'
    }, {
      desc: 'Alert when any expense exceeds your set budget threshold',
      time: 'Passive'
    }, {
      desc: 'Track all accounts payable & flag invoices due within 7 days',
      time: 'Passive'
    }, {
      desc: 'Reconcile Stripe, PayPal & bank accounts monthly',
      time: '2 hrs/month'
    }, {
      desc: 'Alert when revenue is tracking below monthly target',
      time: 'Passive'
    }, {
      desc: 'Log every business expense from a receipt photo automatically',
      time: 'Passive'
    }, {
      desc: 'Monitor recurring subscriptions & flag ones unused for 30+ days',
      time: 'Passive'
    }, {
      desc: 'Generate quarterly tax prep summary report automatically',
      time: '3 hrs/quarter'
    }],
    total: '~12 hrs/week',
    tools: ['QuickBooks', 'Xero', 'Stripe', 'Expensify', 'Google Sheets']
  }]
}, {
  name: 'IT',
  color: 'steel',
  bgClass: 'bg-steel/10',
  borderClass: 'border-steel/20',
  textClass: 'text-steel',
  roles: [{
    title: 'IT & Tech Support',
    hours: '~12 hrs/week',
    icon: <Shield size={24} data-id="element-2740" data-mp="dknfg4t92zfm" />,
    desc: 'Uptime monitoring, backups, security, DNS, troubleshooting',
    tasks: [{
      desc: 'Monitor domain & SSL expiry — alert 60 days out',
      time: 'Passive'
    }, {
      desc: 'Run automated daily backups & verify completion',
      time: 'Passive'
    }, {
      desc: 'Monitor & block suspicious login attempts automatically',
      time: 'Passive'
    }, {
      desc: 'Auto-update plugins & themes on schedule with rollback flag',
      time: 'Passive'
    }, {
      desc: 'Alert when any CDN, storage or bandwidth hits 80% capacity',
      time: 'Passive'
    }, {
      desc: 'Generate all tech asset inventory & report quarterly',
      time: '3 hrs/quarter'
    }, {
      desc: 'Monitor DNS records for unauthorized changes',
      time: 'Passive'
    }, {
      desc: 'Generate weekly security scan & uptime report',
      time: '1 hr/week'
    }, {
      desc: 'Alert when a new device logs into any company account',
      time: 'Passive'
    }, {
      desc: 'Sync business info across all directories when it changes',
      time: 'Passive'
    }],
    total: '~12 hrs/week',
    tools: ['Cloudflare', 'AWS', 'WordPress', 'Datadog']
  }]
}, {
  name: 'Data & Reporting',
  color: 'purple',
  bgClass: 'bg-purple/10',
  borderClass: 'border-purple/20',
  textClass: 'text-purple',
  roles: [{
    title: 'Data & Reporting Analyst',
    hours: '~15 hrs/week',
    icon: <LineChart size={24} data-id="element-2741" data-mp="3303votcsk0t6" />,
    desc: 'Dashboards, performance reports, KPI tracking, trend analysis, insights',
    tasks: [{
      desc: 'Build a live KPI dashboard pulling from all connected platforms',
      time: '3 hrs/week'
    }, {
      desc: 'Auto-flag any trending anomaly or metric spike before broadcast',
      time: 'Passive'
    }, {
      desc: 'Generate monthly client performance report automatically',
      time: '3 hrs/month'
    }, {
      desc: 'Alert when any tracked metric drops below threshold',
      time: 'Passive'
    }, {
      desc: 'Cross-reference results vs. last month on every key metric',
      time: '2 hrs/week'
    }, {
      desc: 'Track traffic, conversion and revenue trends weekly',
      time: '2 hrs/week'
    }, {
      desc: 'Track & report revenue trend by client and service type',
      time: '1 hr/week'
    }, {
      desc: 'Generate a quarterly business review report automatically',
      time: '4 hrs/quarter'
    }, {
      desc: 'Pull ad data from all platforms into one centralized report',
      time: 'Passive'
    }, {
      desc: 'Auto-detect correlation between marketing spend and revenue',
      time: 'Passive'
    }],
    total: '~15 hrs/week',
    tools: ['Google Analytics', 'Looker Studio', 'Google Sheets', 'HubSpot']
  }]
}, {
  name: 'Design & Web',
  color: 'teal',
  bgClass: 'bg-teal/10',
  borderClass: 'border-teal/20',
  textClass: 'text-teal',
  roles: [{
    title: 'Website Designer',
    hours: '~18 hrs/week',
    icon: <Globe size={24} data-id="element-2742" data-mp="3p6biver8eveh" />,
    desc: 'Landing pages, performance, conversion optimization, QA, updates',
    tasks: [{
      desc: 'Monitor page load speed & alert when any page exceeds 3 seconds',
      time: 'Passive'
    }, {
      desc: 'Auto-compress new images on upload to stay under 100KB',
      time: 'Passive'
    }, {
      desc: 'Run weekly broken link scan & generate fix report',
      time: '30 min/week'
    }, {
      desc: 'Track conversion rates by landing page & flag drops weekly',
      time: '1 hr/week'
    }, {
      desc: 'Monitor uptime & alert within 60 seconds if site goes down',
      time: 'Passive'
    }, {
      desc: 'Build new landing pages from approved brand templates',
      time: '2-4 hrs/page'
    }, {
      desc: 'Track A/B test performance across landing page variants',
      time: 'Passive'
    }, {
      desc: 'Update banners, promos & seasonal content on schedule',
      time: '45 min/update'
    }, {
      desc: 'Monitor Core Web Vitals & generate weekly performance report',
      time: '1 hr/week'
    }, {
      desc: 'Generate monthly website health, speed & conversion report',
      time: '2 hrs/month'
    }],
    total: '~18 hrs/week',
    tools: ['WordPress', 'Elementor', 'Google PageSpeed', 'Hotjar']
  }, {
    title: 'Graphics Designer',
    hours: '~15 hrs/week',
    icon: <Palette size={24} data-id="element-2743" data-mp="wwc2n9zah1cs" />,
    desc: 'Social graphics, ad creatives, brand assets, thumbnails, templates',
    tasks: [{
      desc: 'Auto-resize social graphics to exact specs per platform on every export',
      time: 'Passive'
    }, {
      desc: 'Generate on-brand creative variations for A/B testing',
      time: '2 hrs/week'
    }, {
      desc: 'Auto-add brand watermark to every exported image',
      time: 'Passive'
    }, {
      desc: 'Generate video thumbnails from a brand template for every upload',
      time: '15 min/thumbnail'
    }, {
      desc: 'Create branded proposal cover pages & pitch decks',
      time: '1 hr/deck'
    }, {
      desc: 'Resize & optimize all images for web use automatically',
      time: 'Passive'
    }, {
      desc: 'Create email header graphics from campaign brief',
      time: '30 min/email'
    }, {
      desc: 'Generate branded social story & reel frames from templates',
      time: '20 min/set'
    }, {
      desc: 'Auto-generate on-brand quote graphics from testimonials',
      time: 'Passive'
    }, {
      desc: 'Auto-organize & archive all brand assets in digital library',
      time: 'Passive'
    }],
    total: '~15 hrs/week',
    tools: ['Canva', 'Adobe Express', 'Magic Patterns', 'Figma']
  }]
}];
export function TeamOfTwenty() {
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<string>('All');
  const displayedDepartments = activeTab === 'All' ? DEPARTMENTS : DEPARTMENTS.filter(d => d.name === activeTab);
  const allRoleTitles = DEPARTMENTS.flatMap(d => d.roles.map(r => r.title));
  const displayedRoleTitles = displayedDepartments.flatMap(d => d.roles.map(r => r.title));
  const allDisplayedExpanded = displayedRoleTitles.every(t => expandedRoles.has(t));
  function toggleRole(title: string) {
    setExpandedRoles(prev => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  }
  function toggleAll() {
    if (allDisplayedExpanded) {
      setExpandedRoles(new Set());
    } else {
      setExpandedRoles(new Set(allRoleTitles));
    }
  }
  return <main className="pt-20 bg-hero-gradient min-h-screen" data-id="element-2744" data-mp="26z028i5av46">
      {/* 1. Hero Section */}
      <section className="relative pt-24 pb-20 lg:pt-32 lg:pb-28 px-6 bg-hero-gradient overflow-hidden" data-id="element-2745" data-mp="681555yqu1e2">
        {/* Ambient Glow Orbs */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-2746" data-mp="g4np7sj8wof1" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-2747" data-mp="l38eiwwylnop" />

        {/* Massive Background Number */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[20rem] md:text-[30rem] font-black text-white/[0.02] pointer-events-none select-none z-0 leading-none" data-id="element-2748" data-mp="tx3ja3u65c3t">
          20
        </div>

        <div className="inner-container relative z-10 text-center max-w-4xl mx-auto" data-id="element-2749" data-mp="13vt0rvi5kujj">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-2750" data-mp="18tksaoevyen7">
            <span className="eyebrow" data-id="element-2751" data-mp="233mxpyjua2m6">The Team of 20</span>
            <h1 className="font-poppins font-extrabold text-5xl md:text-6xl lg:text-7xl leading-[1.1] mb-6 text-white" data-id="element-2752" data-mp="1y5lg7i22abct">
              The Output of 20 People. <br data-id="element-2753" data-mp="1ah24esjszgfz" />
              <span className="text-gradient" data-id="element-2754" data-mp="1agk51n1ugm3u">The Cost of 2.</span>
            </h1>
            <p className="text-lg md:text-xl text-steel mb-12 leading-relaxed max-w-2xl mx-auto" data-id="element-2755" data-mp="16fqd8ila6n5o">
              For the price of 2 employees, you get a full AI-powered team that
              never stops working.
            </p>

            {/* Stats Bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 max-w-3xl mx-auto bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm" data-id="element-2756" data-mp="rp9fpabrohe2">
              <div data-id="element-2757" data-mp="mq6anpvseb63">
                <div className="text-3xl font-bold text-white font-poppins mb-1" data-id="element-2758" data-mp="2ydt9acuc9k9a">
                  20
                </div>
                <div className="text-xs text-steel uppercase tracking-wider font-medium" data-id="element-2759" data-mp="3351lfgn1zjq5">
                  Roles
                </div>
              </div>
              <div data-id="element-2760" data-mp="2pb0h87dc14ie">
                <div className="text-3xl font-bold text-white font-poppins mb-1" data-id="element-2761" data-mp="2uc7q69r7o9qn">
                  700+
                </div>
                <div className="text-xs text-steel uppercase tracking-wider font-medium" data-id="element-2762" data-mp="3i1isvws4ge7f">
                  Tasks Automated
                </div>
              </div>
              <div data-id="element-2763" data-mp="3m11b2ksk6b6k">
                <div className="text-3xl font-bold text-white font-poppins mb-1" data-id="element-2764" data-mp="3844d7ze0vtg6">
                  385+
                </div>
                <div className="text-xs text-steel uppercase tracking-wider font-medium" data-id="element-2765" data-mp="3d1mlroo4o6nn">
                  Hours / Week
                </div>
              </div>
              <div data-id="element-2766" data-mp="21n95b4vpethn">
                <div className="text-3xl font-bold text-white font-poppins mb-1" data-id="element-2767" data-mp="21nsnmmp9njw7">
                  24/7
                </div>
                <div className="text-xs text-steel uppercase tracking-wider font-medium" data-id="element-2768" data-mp="1jyplojw2s9e3">
                  Never Stops
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-2769" data-mp="1f0o27xuqm823" />

      {/* 2. Intro Text Section */}
      <section className="py-20 relative" data-id="element-2770" data-mp="22s3983xgldjo">
        <div className="inner-container text-center max-w-3xl mx-auto px-6" data-id="element-2771" data-mp="22oyzys54zrm5">
          <h2 className="h2-title text-white mb-4" data-id="element-2772" data-mp="27p3ewdqecea9">
            Every Role. Every Task.{' '}
            <span className="text-gradient" data-id="element-2773" data-mp="1tptzvhdxfq7v">All Handled.</span>
          </h2>
          <p className="text-xl text-steel mb-6 font-medium" data-id="element-2774" data-mp="1xqnko1w2oowj">
            Scroll through and ask yourself — who on your team is doing all of
            this right now?
          </p>
          <p className="text-steel/70 leading-relaxed" data-id="element-2775" data-mp="mcs890wjyu90">
            What you're about to see is exactly what MISTR executes. Every task
            below shows how long it would take a real employee to do it
            manually. Many of these tasks are marked{' '}
            <strong className="text-teal font-semibold" data-id="element-2776" data-mp="or6us0xe293p">Passive</strong> —
            meaning they run in the background 24 hours a day, 7 days a week,
            with zero human involvement.
          </p>
        </div>
      </section>

      {/* 3 & 4. Roles Grid with Tabs */}
      <section className="pb-24 pt-8 bg-white/[0.02] relative" data-id="element-2777" data-mp="3s86zqsvzu3n8">
        <div className="inner-container px-6" data-id="element-2778" data-mp="3n9vl84ur74yo">
          {/* Floating Expand/Collapse All Button */}
          <motion.button onClick={toggleAll} initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="fixed bottom-8 right-8 z-50 flex items-center gap-2 text-sm font-medium text-white bg-purple hover:bg-purple/90 border border-purple/30 px-5 py-3 rounded-full shadow-lg shadow-purple/25 hover:shadow-xl hover:shadow-purple/35 transition-all backdrop-blur-sm" data-id="element-2779" data-mp="5te0to4qt9ig">
            {allDisplayedExpanded ? 'Collapse All' : 'Expand All'}
            <motion.div animate={{
            rotate: allDisplayedExpanded ? 180 : 0
          }} data-id="element-2780" data-mp="6ufo7wv8oepe">
              <ChevronDown size={16} data-id="element-2781" data-mp="buli7jozedan" />
            </motion.div>
          </motion.button>

          <div className="flex overflow-x-auto gap-3 pb-8 mb-8 scrollbar-hide justify-start xl:justify-center" data-id="element-2782" data-mp="3b9fgfh87u9d1">
            <button onClick={() => setActiveTab('All')} className={`px-5 py-2.5 rounded-full whitespace-nowrap font-medium transition-all ${activeTab === 'All' ? 'bg-purple text-white shadow-md shadow-purple/20' : 'bg-white/10 text-steel hover:bg-white/15 hover:text-white'}`} data-id="element-2783" data-mp="3fbahqlq5cmqt">
              All Departments
            </button>
            {DEPARTMENTS.map(dept => <button key={dept.name} onClick={() => setActiveTab(dept.name)} className={`px-5 py-2.5 rounded-full whitespace-nowrap font-medium transition-all ${activeTab === dept.name ? 'bg-purple text-white shadow-md shadow-purple/20' : 'bg-white/10 text-steel hover:bg-white/15 hover:text-white'}`} data-id="element-2784" data-mp="2oz7cvhzmfonx">
                {dept.name}
              </button>)}
          </div>

          <div className="space-y-6 max-w-4xl mx-auto" data-id="element-2785" data-mp="269r1fzmeebsq">
            {displayedDepartments.map(dept => <div key={dept.name} className="space-y-6" data-id="element-2786" data-mp="218tel9qg0tkw">
                {dept.roles.map(role => {
              const isExpanded = expandedRoles.has(role.title);
              return <motion.div key={role.title} layout initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-50px'
              }} className={`bg-white/5 border rounded-2xl overflow-hidden transition-all duration-300 ${isExpanded ? `border-white/20` : `border-white/10 hover:border-white/20`}`} data-id="element-2787" data-mp="1rczwzbxva9w9">
                      {/* Collapsed State Header (Always visible) */}
                      <div className="p-6 md:p-8 cursor-pointer flex flex-col md:flex-row md:items-start justify-between gap-6" onClick={() => toggleRole(role.title)} data-id="element-2788" data-mp="3fqmmoeki26uq">
                        <div className="flex gap-5 items-start" data-id="element-2789" data-mp="360uprdoxh1n3">
                          <div className={`w-14 h-14 rounded-xl shrink-0 flex items-center justify-center ${dept.bgClass} ${dept.textClass}`} data-id="element-2790" data-mp="2s0k0gf39nv41">
                            {role.icon}
                          </div>
                          <div data-id="element-2791" data-mp="28f0acp4t2p2z">
                            <div className="flex items-center gap-3 mb-2" data-id="element-2792" data-mp="28g1wbond0vzh">
                              <h3 className="font-poppins font-bold text-xl md:text-2xl text-white" data-id="element-2793" data-mp="2mfayvlin7rkz">
                                {role.title}
                              </h3>
                              <span className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full ${dept.bgClass} ${dept.textClass}`} data-id="element-2794" data-mp="2wcz7nfd32zm5">
                                {dept.name}
                              </span>
                            </div>
                            <p className="text-steel text-sm md:text-base leading-relaxed max-w-xl" data-id="element-2795" data-mp="313fkax92yh7g">
                              {role.desc}
                            </p>
                            {!isExpanded && <div className="flex items-center gap-3 mt-3" data-id="element-2796" data-mp="3a5x7pf0h1fw1">
                                <span className="text-xs font-medium text-steel bg-white/10 px-2.5 py-1 rounded-md" data-id="element-2797" data-mp="27rzbl6n82d3g">
                                  {role.tasks.length} tasks
                                </span>
                                <span className="text-xs font-medium text-teal bg-teal/10 px-2.5 py-1 rounded-md flex items-center gap-1.5" data-id="element-2798" data-mp="2gqujqo9ttx9e">
                                  <span className="w-1.5 h-1.5 rounded-full bg-teal animate-pulse" data-id="element-2799" data-mp="2qhfpqwrty42c" />
                                  {role.tasks.filter(t => t.time === 'Passive').length}{' '}
                                  passive
                                </span>
                              </div>}
                          </div>
                        </div>

                        <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-start gap-4 shrink-0" data-id="element-2800" data-mp="1a3yzkf8hdwi1">
                          <div className="text-right" data-id="element-2801" data-mp="12lho43h1o7mf">
                            <div className="text-2xl font-bold text-white font-poppins" data-id="element-2802" data-mp="xs6czq5794op">
                              {role.hours}
                            </div>
                            <div className="text-xs text-steel/70 uppercase tracking-wider" data-id="element-2803" data-mp="nv1wa5u4i1fa">
                              Saved per week
                            </div>
                          </div>
                          <button className="flex items-center gap-2 text-sm font-medium text-steel hover:text-white transition-colors bg-white/10 hover:bg-white/15 px-4 py-2 rounded-lg" data-id="element-2804" data-mp="1gixuxg3963ep">
                            {isExpanded ? 'Hide tasks' : 'View all tasks'}
                            <motion.div animate={{
                        rotate: isExpanded ? 180 : 0
                      }} data-id="element-2805" data-mp="17kuayo7x1ep0">
                              <ChevronDown size={16} data-id="element-2806" data-mp="3sbcvdyvudjy9" />
                            </motion.div>
                          </button>
                        </div>
                      </div>

                      {/* Expanded State (Task Table) */}
                      <AnimatePresence data-id="element-2807" data-mp="1m8poaezuu41z">
                        {isExpanded && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeInOut'
                  }} className="border-t border-white/10 bg-white/5" data-id="element-2808" data-mp="1r691rcoqacsv">
                            {/* Table Header */}
                            <div className="grid grid-cols-[1fr_auto] gap-4 px-6 md:px-8 py-4 border-b border-white/10 text-xs font-bold uppercase tracking-wider text-steel/50" data-id="element-2809" data-mp="37y367z0ddf71">
                              <div data-id="element-2810" data-mp="3gqy2423y5yk5">Task</div>
                              <div className="text-right" data-id="element-2811" data-mp="3gs806rasv6qs">Avg Time</div>
                            </div>

                            {/* Task Rows */}
                            <div className="divide-y divide-white/5" data-id="element-2812" data-mp="2p0s2o27pfzls">
                              {role.tasks.map((task, i) => <div key={i} className="grid grid-cols-[1fr_auto] gap-4 px-6 md:px-8 py-4 items-center hover:bg-white/5 transition-colors" data-id="element-2813" data-mp="331mfqy5p6oqs" data-mpi={i}>
                                  <div className="flex items-start gap-3" data-id="element-2814" data-mp="db90x9ysfbrq">
                                    <div className={`mt-2 w-1.5 h-1.5 rounded-full shrink-0 ${dept.bgClass.replace('/10', '')}`} data-id="element-2815" data-mp="dd3kokyboxu8" />
                                    <span className="text-sm text-steel leading-relaxed" data-id="element-2816" data-mp="1taq2ekjqqnvy">
                                      {task.desc}
                                    </span>
                                  </div>
                                  <div className="text-right shrink-0" data-id="element-2817" data-mp="2l2fcnvive5op">
                                    {task.time === 'Passive' ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-teal/10 text-teal text-xs font-semibold tracking-wide" data-id="element-2818" data-mp="2h0u4uol70hld">
                                        <div className="w-1.5 h-1.5 rounded-full bg-teal animate-pulse" data-id="element-2819" data-mp="27bai37ud03l8" />
                                        PASSIVE
                                      </span> : <span className="text-sm font-medium text-steel/70" data-id="element-2820" data-mp="2zx3ff1wvyq0m">
                                        {task.time}
                                      </span>}
                                  </div>
                                </div>)}
                            </div>

                            {/* Weekly Total Bar */}
                            <div className={`px-6 md:px-8 py-5 flex items-center justify-between border-t border-white/10 ${dept.bgClass}`} data-id="element-2821" data-mp="3q96uszl1f6hr">
                              <div className={`font-bold uppercase tracking-wider text-sm ${dept.textClass}`} data-id="element-2822" data-mp="3tm46v3b8yrz">
                                {role.title} Weekly Total
                              </div>
                              <div className={`font-poppins font-bold text-lg ${dept.textClass}`} data-id="element-2823" data-mp="cw3srg6fu3i5">
                                {role.total}
                              </div>
                            </div>

                            {/* Powered By Tools */}
                            <div className="px-6 md:px-8 py-4 flex flex-wrap items-center gap-3 border-t border-white/10 bg-white/5" data-id="element-2824" data-mp="2nmbdsrer28a8">
                              <span className="text-xs font-bold uppercase tracking-wider text-steel/50" data-id="element-2825" data-mp="2ivczfxdbnk9m">
                                Powered By
                              </span>
                              {role.tools.map((tool, i) => <span key={i} className="text-xs font-medium text-steel bg-white/10 border border-white/10 px-2.5 py-1 rounded-md shadow-sm" data-id="element-2826" data-mp="2sniqn4vr1xnr" data-mpi={i}>
                                  {tool}
                                </span>)}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </motion.div>;
            })}
              </div>)}
          </div>
        </div>
      </section>

      {/* 5. How They Work Together */}
      <section className="py-24 bg-dark-bg relative overflow-hidden border-t border-white/5" data-id="element-2827" data-mp="3a10nv8icd14n">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal/5 rounded-full blur-[120px] pointer-events-none" data-id="element-2828" data-mp="3e2vpyf83jijh" />

        <div className="inner-container relative z-10 px-6" data-id="element-2829" data-mp="3551s2zuoogu7">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2830" data-mp="2r48xkf4dtlr3">
            <span className="eyebrow" data-id="element-2831" data-mp="2r623ttb323yk">A Unified Intelligence</span>
            <h2 className="h2-title text-white" data-id="element-2832" data-mp="2v52n1cxh38m3">How They Work Together</h2>
            <p className="text-lg text-steel" data-id="element-2833" data-mp="pd5kzkaxlx3w">
              Unlike human employees, MISTR's roles aren't siloed. They share a
              single, perfect memory of your entire business.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-2834" data-mp="4o3c2bof8v07">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:border-teal/30 transition-colors" data-id="element-2835" data-mp="4lh5ae9cujj1">
              <div className="w-14 h-14 bg-teal/10 rounded-xl flex items-center justify-center text-teal mb-6" data-id="element-2836" data-mp="3d3mx4nexv746">
                <Database size={28} data-id="element-2837" data-mp="3840m8kml22yj" />
              </div>
              <h3 className="font-poppins font-semibold text-xl text-white mb-3" data-id="element-2838" data-mp="382fs2w46km17">
                Shared Memory
              </h3>
              <p className="text-steel leading-relaxed" data-id="element-2839" data-mp="2y64em2sutq56">
                All 20 roles share the exact same context. When your Sales Rep
                learns something new about a client, your Support Rep instantly
                knows it too.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:border-purple/30 transition-colors" data-id="element-2840" data-mp="1doboyz2n007p">
              <div className="w-14 h-14 bg-purple/10 rounded-xl flex items-center justify-center text-purple mb-6" data-id="element-2841" data-mp="uvhi6sf63eek">
                <ArrowRightLeft size={28} data-id="element-2842" data-mp="14ndhu7wwjckw" />
              </div>
              <h3 className="font-poppins font-semibold text-xl text-white mb-3" data-id="element-2843" data-mp="14oyec32s15le">
                Seamless Handoffs
              </h3>
              <p className="text-steel leading-relaxed" data-id="element-2844" data-mp="c72t4aoeuage">
                Marketing generates the lead, Sales works it, and Support
                handles the onboarding. The handoff happens instantly, with zero
                friction or lost data.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:border-teal/30 transition-colors" data-id="element-2845" data-mp="dwgbarx3s8vi">
              <div className="w-14 h-14 bg-teal/10 rounded-xl flex items-center justify-center text-teal mb-6" data-id="element-2846" data-mp="3r8zxr19iyzk9">
                <LayoutDashboard size={28} data-id="element-2847" data-mp="16l5483o097w6" />
              </div>
              <h3 className="font-poppins font-semibold text-xl text-white mb-3" data-id="element-2848" data-mp="16jajoqgnbhsm">
                One Dashboard
              </h3>
              <p className="text-steel leading-relaxed" data-id="element-2849" data-mp="2jjnj13cqz7xu">
                No more chasing updates across Slack, email, and PM tools. See
                exactly what every "department" is doing from one centralized
                command center.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 6. Social Proof / Testimonial */}
      <section className="py-24 bg-[#0B0D14] relative overflow-hidden border-t border-white/5" data-id="element-2850" data-mp="2fk6ag120xuog">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple/10 rounded-full blur-[100px] pointer-events-none" data-id="element-2851" data-mp="338g0jf69t4le" />
        <div className="inner-container relative z-10 max-w-4xl mx-auto text-center px-6" data-id="element-2852" data-mp="2tgs8pd5sxlje">
          <Quote size={48} className="text-purple/40 mx-auto mb-8" data-id="element-2853" data-mp="2yg4vjxwoaslz" />
          <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white leading-tight mb-8" data-id="element-2854" data-mp="3m6ik3eg1u9bt">
            "I used to spend $40,000 a month on an agency and a scattered team
            of freelancers. Half the time, I was still doing the work myself.
            MISTR replaced all of them in one afternoon. My overhead vanished,
            and things actually get done now."
          </h2>
          <div className="flex items-center justify-center gap-4" data-id="element-2855" data-mp="1h5686f8fkgp4">
            <div className="w-12 h-12 bg-white/10 rounded-full overflow-hidden border border-white/20" data-id="element-2856" data-mp="1h5g048d1cxxg">
              <img src="https://i.pravatar.cc/150?u=a042581f4e29026704d" alt="Sarah Jenkins" className="w-full h-full object-cover" data-id="element-2857" data-mp="1c40cq9ndyfbw" />
            </div>
            <div className="text-left" data-id="element-2858" data-mp="24sxqbzgnjnyb">
              <div className="font-poppins font-semibold text-white" data-id="element-2859" data-mp="1zumjlxlnltkb">
                Sarah Jenkins
              </div>
              <div className="text-sm text-steel" data-id="element-2860" data-mp="1vtj4jdbfk1k5">
                Founder, Elevate E-Commerce
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Final CTA */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-2861" data-mp="kdcdj359vk0v" />
      <section className="relative py-32 px-6 bg-hero-gradient overflow-hidden flex items-center justify-center text-center" data-id="element-2862" data-mp="2w3k8y0p9miuy">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2863" data-mp="301rpaufthi5m">
          <div className="absolute top-0 left-0 w-full h-full bg-dark-bg/50" data-id="element-2864" data-mp="3525lnf8233nt" />
          <motion.div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple/30 rounded-full blur-[100px]" animate={{
          x: [0, 50, 0],
          y: [0, 30, 0]
        }} transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-2865" data-mp="39txczrz1ddf0" />
          <motion.div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal/20 rounded-full blur-[100px]" animate={{
          x: [0, -40, 0],
          y: [0, -50, 0]
        }} transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-2866" data-mp="3nrm076gkswn8" />
        </div>

        <div className="inner-container relative z-10 max-w-4xl" data-id="element-2867" data-mp="1i9lds05yarn2">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-2868" data-mp="1n7wmu8of8y48">
            <h2 className="font-poppins font-extrabold text-5xl md:text-6xl mb-10 leading-tight text-white" data-id="element-2869" data-mp="20yi6hzlp9c36">
              Your Business. <br data-id="element-2870" data-mp="25wbft1iaum7o" />
              <span className="text-gradient" data-id="element-2871" data-mp="25vrwbgd03prj">Running Itself.</span>
            </h2>

            {/* CTA Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12 bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-sm" data-id="element-2872" data-mp="2aucula8ozzdc">
              <div data-id="element-2873" data-mp="giy9j6qmmo3x">
                <div className="text-2xl md:text-3xl font-bold text-white font-poppins mb-1" data-id="element-2874" data-mp="gjq2e9sua7qi">
                  20
                </div>
                <div className="text-[10px] md:text-xs text-steel uppercase tracking-wider font-medium" data-id="element-2875" data-mp="ljlxtknh0qug">
                  Roles Replaced
                </div>
              </div>
              <div data-id="element-2876" data-mp="2tzhnow3qrcsz">
                <div className="text-2xl md:text-3xl font-bold text-white font-poppins mb-1" data-id="element-2877" data-mp="2tz7yhaqit9oc">
                  700+
                </div>
                <div className="text-[10px] md:text-xs text-steel uppercase tracking-wider font-medium" data-id="element-2878" data-mp="2l15qgb8ddgrk">
                  Tasks / Week
                </div>
              </div>
              <div data-id="element-2879" data-mp="3dp1uk109qayd">
                <div className="text-2xl md:text-3xl font-bold text-white font-poppins mb-1" data-id="element-2880" data-mp="3dos5cfn1s7tq">
                  385+
                </div>
                <div className="text-[10px] md:text-xs text-steel uppercase tracking-wider font-medium" data-id="element-2881" data-mp="38o44l290ocmb">
                  Hours / Week
                </div>
              </div>
              <div data-id="element-2882" data-mp="1xbtn3rzq42x6">
                <div className="text-2xl md:text-3xl font-bold text-white font-poppins mb-1" data-id="element-2883" data-mp="1xc3cbdcy261t">
                  24/7
                </div>
                <div className="text-[10px] md:text-xs text-steel uppercase tracking-wider font-medium" data-id="element-2884" data-mp="1tt46kq4rclog">
                  Never Stops
                </div>
              </div>
            </div>

            <button className="btn-primary text-lg px-10 py-5 mb-6 shadow-[0_0_40px_rgba(124,58,237,0.4)] hover:shadow-[0_0_60px_rgba(124,58,237,0.6)] transition-shadow flex items-center gap-2 mx-auto" data-id="element-2885" data-mp="1ks6cazk4fsj2">
              Book a Free Strategy Call <ArrowRight size={20} data-id="element-2886" data-mp="1b24li00dvw0g" />
            </button>

            <p className="text-sm text-steel/70 font-medium" data-id="element-2887" data-mp="1b0js4dpt2j46">
              See what we'd build for you. No contracts. No setup fees.
            </p>
          </motion.div>
        </div>
      </section>
    </main>;
}